import React, { FC, Suspense, lazy } from "react";

import { Translate } from "@/app/application/Translate";
import { parseUtc } from "@/features/training/lib/trainingDateUtils";
import { TrainingSessionStatus } from "@/features/training/model/Training/TrainingSession";
import { CoursesRightPanelSkeleton } from "@/features/training/pages/training-tab/CoursesSkeletons";
import { UpcomingSessionItem } from "@/features/training/ui";
import { PaginatedSearchWrapper } from "@/shared/ui";
import ComponentError from "@/shared/ui/errors/Components";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as Sentry from "@sentry/react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import dayjs from "dayjs";

import {
	useFetchCourseSessions,
	useFetchDrillSessions,
} from "@/service/TrainingQueries";

const ActiveItemTableSkeleton = lazy(
	() => import("@/shared/ui/loaders/ActiveItemTableSkeleton"),
);

interface UpcomingSessionsProps {
	type: "drill" | "course";
}

const UpcomingSessionsEmptyState: FC = () => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			flex: 1,
			minHeight: 280,
			px: 3,
			textAlign: "center",
		}}
	>
		<CalendarMonthOutlinedIcon
			sx={{ fontSize: 48, color: "text.disabled", mb: 1.5 }}
		/>
		<Typography
			variant="h6"
			fontWeight={600}
			color="text.secondary"
			gutterBottom
		>
			<Translate String="Training.UpcomingSessions.Empty.Title">
				No active sessions
			</Translate>
		</Typography>
		<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
			<Translate String="Training.UpcomingSessions.Empty.Description">
				There are no upcoming or overdue sessions at the moment.
			</Translate>
		</Typography>
	</Box>
);

export const UpcomingSessions: FC<UpcomingSessionsProps> = ({ type }) => {
	const queryOptions = { Status: TrainingSessionStatus.Active };
	const isCourseSessionsActive = type === "course";
	const { data: courseSessions, isLoading: isLoadingCourse } =
		useFetchCourseSessions(queryOptions, isCourseSessionsActive);
	const { isLoading: isLoadingDrill, data: drillSessions } =
		useFetchDrillSessions(queryOptions, !isCourseSessionsActive);

	const sessions = isCourseSessionsActive ? courseSessions : drillSessions;
	const isLoading = isCourseSessionsActive ? isLoadingCourse : isLoadingDrill;

	const upcomingSessions = Array.isArray(sessions)
		? sessions.filter((item) => parseUtc(item.Date).isAfter(dayjs()))
		: [];

	const overdueSessions = Array.isArray(sessions)
		? sessions.filter((item) => parseUtc(item.Date).isBefore(dayjs()))
		: [];

	const hasAnySessions =
		upcomingSessions.length > 0 || overdueSessions.length > 0;

	if (isLoading) {
		return (
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<CoursesRightPanelSkeleton />
			</Box>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<Box
				sx={{
					flexShrink: 0,
					py: 1.75,
					px: { xs: 2, sm: 2.5 },
					borderBottom: 1,
					borderColor: "divider",
				}}
			>
				<Typography variant="h6" fontWeight={700}>
					<Translate String="Training.UpcomingSessions">Sessions</Translate>
				</Typography>
				{hasAnySessions && (
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{type === "course" ? (
							<Translate String="Training.UpcomingSessions.Courses.Subtitle">
								Upcoming and overdue course sessions
							</Translate>
						) : (
							<Translate String="Training.UpcomingSessions.Drills.Subtitle">
								Upcoming and overdue drill sessions
							</Translate>
						)}
					</Typography>
				)}
			</Box>

			{/* Content */}
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					overflowX: "hidden",
					scrollbarGutter: "stable both-edges",
					overscrollBehavior: "contain",
					px: { xs: 2, sm: 2.5 },
					py: { xs: 1.5, sm: 2 },
					"&::-webkit-scrollbar": { width: 8 },
					"&::-webkit-scrollbar-track": {
						backgroundColor: (t) =>
							alpha(
								t.palette.mode === "dark"
									? t.palette.background.default
									: t.palette.text.primary,
								t.palette.mode === "dark" ? 0.5 : 0.05,
							),
						borderRadius: 8,
					},
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: (t) =>
							alpha(
								t.palette.text.primary,
								t.palette.mode === "dark" ? 0.3 : 0.2,
							),
						borderRadius: 8,
					},
				}}
			>
				{!hasAnySessions ? (
					<UpcomingSessionsEmptyState />
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
						{upcomingSessions.length > 0 && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8, display: "block", mb: 1.5 }}
								>
									<Translate String="Training.UpcomingSessions.Section.Upcoming">
										Upcoming
									</Translate>{" "}
									({upcomingSessions.length})
								</Typography>
								<QueryErrorResetBoundary>
									{({ reset }) => (
										<Sentry.ErrorBoundary
											onReset={reset}
											fallback={(fallbackProps) => (
												<ComponentError
													message={
														fallbackProps.error instanceof Error
															? fallbackProps.error.message
															: String(fallbackProps.error)
													}
													reset={() => {
														fallbackProps.resetError();
													}}
												/>
											)}
										>
											<Suspense fallback={<ActiveItemTableSkeleton />}>
												<PaginatedSearchWrapper
													items={upcomingSessions}
													itemsPerPage={10}
													getItemName={(item) => item.Name}
													getItemDate={(item) => item.Date}
												>
													{(items) => (
														<Box
															sx={{
																display: "flex",
																flexDirection: "column",
																gap: 1,
															}}
														>
															{items.map((item, key) => (
																<UpcomingSessionItem session={item} key={key} />
															))}
														</Box>
													)}
												</PaginatedSearchWrapper>
											</Suspense>
										</Sentry.ErrorBoundary>
									)}
								</QueryErrorResetBoundary>
							</Box>
						)}

						{overdueSessions.length > 0 && (
							<Box>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ letterSpacing: 0.8, display: "block", mb: 1.5 }}
								>
									<Translate String="Training.OverdueSessions">
										Overdue Sessions
									</Translate>{" "}
									({overdueSessions.length})
								</Typography>
								<QueryErrorResetBoundary>
									{({ reset }) => (
										<Sentry.ErrorBoundary
											onReset={reset}
											fallback={(fallbackProps) => (
												<ComponentError
													message={
														fallbackProps.error instanceof Error
															? fallbackProps.error.message
															: String(fallbackProps.error)
													}
													reset={() => {
														fallbackProps.resetError();
													}}
												/>
											)}
										>
											<Suspense fallback={<ActiveItemTableSkeleton />}>
												<PaginatedSearchWrapper
													items={overdueSessions}
													itemsPerPage={10}
													getItemName={(item) => item.Name}
													getItemDate={(item) => item.Date}
												>
													{(items) => (
														<Box
															sx={{
																display: "flex",
																flexDirection: "column",
																gap: 1,
															}}
														>
															{items.map((item, key) => (
																<UpcomingSessionItem session={item} key={key} />
															))}
														</Box>
													)}
												</PaginatedSearchWrapper>
											</Suspense>
										</Sentry.ErrorBoundary>
									)}
								</QueryErrorResetBoundary>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};
